import React, { useEffect, useState, useCallback } from 'react';
import './TimestampIntersectionInfo.css';

/**
 * Component that checks if the plot's time range intersects with any events
 * from the timestamps database and displays them in an info box
 */
const TimestampIntersectionInfo = ({ startTime, duration, zd, showEvents = false, onEventsCountChange }) => {
    const [intersectingEvents, setIntersectingEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [allEvents, setAllEvents] = useState([]);
    const [isOpen, setIsOpen] = useState(false);

    // Load timestamps on mount
    useEffect(() => {
        fetch('/api/timestamps.json')
            .then(response => {
                if (!response.ok) {
                    throw new Error('Failed to load timestamps');
                }
                return response.json();
            })
            .then(data => {
                setAllEvents(data);
                setLoading(false);
            })
            .catch(err => {
                console.error('Error loading timestamps:', err);
                setError(err.message);
                setLoading(false);
            });
    }, []);

    // Check for intersections whenever time range changes
    useEffect(() => {
        if (!startTime || !duration || allEvents.length === 0) {
            setIntersectingEvents([]);
            return;
        }

        const start = new Date(startTime);
        // Apply timezone offset (zd) just like resetTitle does
        start.setHours(start.getHours() + (parseInt(zd) || 0));
        
        // Calculate end time from duration
        // duration > 12 means minutes, otherwise hours
        const end = new Date(start);
        const durationNum = parseInt(duration) || 0;
        if (durationNum > 12) {
            end.setMinutes(end.getMinutes() + durationNum);
        } else {
            end.setHours(end.getHours() + durationNum);
        }
        end.setSeconds(end.getSeconds() - 1);

        console.log('TimestampIntersectionInfo: Raw props received:', {
            startTime,
            duration,
            zd,
            startTimeType: typeof startTime,
            durationType: typeof duration
        });
        console.log('TimestampIntersectionInfo: Checking range', {
            start: start.toISOString(),
            end: end.toISOString(),
            totalEvents: allEvents.length
        });

        // Find events that intersect with the plot time range
        let debugCount = 0;
        const intersecting = allEvents.filter(event => {
            if (!event.parsedDate) return false;

            const eventDate = new Date(event.parsedDate);

            // Check if event falls within the plot range
            // For date-only events (midnight 00:00:00), treat as spanning the whole day
            // Check both with and without milliseconds: T00:00:00Z and T00:00:00.000Z
            const eventHasTime = event.parsedDate.includes('T') &&
                                 !event.parsedDate.endsWith('T00:00:00.000Z') &&
                                 !event.parsedDate.endsWith('T00:00:00Z');

            if (!eventHasTime) {
                // For date-only events, check if the calendar date matches any day in the plot range
                // Extract just the date part (YYYY-MM-DD) for comparison
                const eventYMD = event.parsedDate.substring(0, 10); // "2023-10-07"
                const startYMD = start.toISOString().substring(0, 10);
                const endYMD = end.toISOString().substring(0, 10);
                
                const matches = eventYMD >= startYMD && eventYMD <= endYMD;
                
                // Debug logging - show matches plus first 10
                if (matches || debugCount < 10) {
                    console.log(`[${debugCount}] Date-only: ${eventYMD} in [${startYMD} to ${endYMD}]? ${matches} DOI: ${event.doi}`);
                    debugCount++;
                }
                
                // Event date must be between start date and end date (inclusive)
                return matches;
            } else {
                // For events with specific times, use exact intersection (no buffer)
                // Event time must fall within the plot range
                const matches = eventDate >= start && eventDate <= end;
                
                if (matches || debugCount < 10) {
                    console.log(`[${debugCount}] Time-specific: ${event.parsedDate} in range? ${matches} DOI: ${event.doi}`);
                    debugCount++;
                }
                
                return matches;
            }
        });

        console.log('TimestampIntersectionInfo: Found', intersecting.length, 'intersecting events');
        setIntersectingEvents(intersecting);
        
        // Notify parent of events count change
        if (onEventsCountChange) {
            onEventsCountChange(intersecting.length);
        }
    }, [startTime, duration, zd, allEvents, onEventsCountChange]);

    console.log('TimestampIntersectionInfo: Render check', {
        loading,
        error,
        intersectingEventsCount: intersectingEvents.length,
        allEventsCount: allEvents.length,
        showEvents
    });

    if (loading) {
        console.log('TimestampIntersectionInfo: Returning null - loading');
        return null; // Don't show anything while loading
    }

    if (error) {
        console.log('TimestampIntersectionInfo: Returning null - error:', error);
        return null; // Silently fail, don't disrupt the user experience
    }

    console.log('TimestampIntersectionInfo: Rendering! Events:', intersectingEvents.length, 'Show:', showEvents);

    // Don't show anything if events are not being displayed
    if (!showEvents || intersectingEvents.length === 0) {
        return null;
    }

    const formatDate = (isoString) => {
        const date = new Date(isoString);
        const hasTime = !isoString.endsWith('T00:00:00.000Z');

        if (hasTime) {
            return date.toISOString().replace('T', ' ').substring(0, 19) + ' UT';
        } else {
            return date.toISOString().substring(0, 10);
        }
    };

    return (
        <div className="timestamp-intersection-wrapper">
            <button
                className={'timestamp-intersection-toggle' + (isOpen ? ' open' : '')}
                onClick={() => setIsOpen(o => !o)}
            >
                <span className="timestamp-intersection-toggle-icon">📅</span>
                <span className="timestamp-intersection-toggle-label">
                    {intersectingEvents.length} notable event{intersectingEvents.length !== 1 ? 's' : ''} in this time range
                </span>
                <span className="timestamp-intersection-toggle-caret">{isOpen ? '▲' : '▼'}</span>
            </button>

            {isOpen && (
                <div className="timestamp-intersection-container">
                    <div className="timestamp-intersection-events">
                        {intersectingEvents.map((event, index) => (
                            <div key={index} className="timestamp-intersection-event">
                                <div className="timestamp-intersection-event-time">
                                    <strong>📍 {formatDate(event.parsedDate)}</strong>
                                    {event.keyTimes && (
                                        <span className="timestamp-intersection-keytimes">
                                            Key times: {event.keyTimes}
                                        </span>
                                    )}
                                </div>
                                {event.event_description && (
                                    <div className="timestamp-intersection-event-description">
                                        {event.event_description}
                                    </div>
                                )}

                                {event.caption_text && (
                                    <div className="timestamp-intersection-event-caption">
                                        <em>Figure/Caption: {event.caption_text}</em>
                                    </div>
                                )}

                                <div className="timestamp-intersection-event-meta">
                                    {(event.doi_link || event.doi) && (
                                        <a
                                            href={event.doi_link || `https://doi.org/${event.doi}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="timestamp-intersection-doi"
                                        >
                                            {event.doi || event.doi_link}
                                        </a>
                                    )}
                                    {event.source && (
                                        <span className="timestamp-intersection-source" title={event.source}>
                                            {event.source.substring(0, 80)}{event.source.length > 80 ? '...' : ''}
                                        </span>
                                    )}
                                    {event.citedBy !== null && event.citedBy !== undefined && (
                                        <span className="timestamp-intersection-cited" title="Citations in database">
                                            Cited: {event.citedBy}
                                        </span>
                                    )}
                                </div>
                            </div>
                ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TimestampIntersectionInfo;
